"use client";

/**
 * Building blocks shared by the staff calling campaigns (member win-back and
 * candidate outreach): chips, contact lines, the outcome + note form and the
 * outreach timeline. Each campaign passes its own outcome vocabulary.
 */
import * as React from "react";
import { Check, Copy, Mail, Phone } from "lucide-react";

import { cn } from "@/lib/utils";
import { fmtDateTime } from "@/lib/insights/format";
import { parseIsoDate } from "@/components/ui/date-field";
import type { OutcomeDef, OutreachEntryBase, Tone } from "@/lib/campaigns/types";

export const TONE: Record<Tone, string> = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  amber: "bg-amber-50 text-amber-800 ring-amber-600/20",
  steel: "bg-[#e6f0f1] text-[#0b5560] ring-[#007a8a]/25",
  red: "bg-red-50 text-[#ae232d] ring-red-600/20",
  slate: "bg-slate-100 text-slate-600 ring-slate-500/20",
  violet: "bg-violet-50 text-violet-700 ring-violet-600/20",
};

export function Chip({ tone, children, className, title }: { tone: Tone; children: React.ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", TONE[tone], className)}>
      {children}
    </span>
  );
}

export function OutcomeChipFor<K extends string>({ outcomes, outcome, long }: { outcomes: OutcomeDef<K>[]; outcome: K; long?: boolean }) {
  const o = outcomes.find((x) => x.key === outcome);
  if (!o) return null;
  return <Chip tone={o.tone}>{long ? o.label : o.short}</Chip>;
}

/**
 * A date-only ISO string ("2026-10-04") as "4 Oct 2026". Parsed as a local
 * date: `new Date("2026-10-04")` is UTC midnight, which shows the day before
 * anywhere west of Greenwich.
 */
export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseIsoDate(iso.slice(0, 10));
  return d ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : iso;
}

export function personName(r: { first_name: string | null; last_name: string | null; member_id: number }, noun = "Member"): string {
  return [r.first_name, r.last_name].filter(Boolean).join(" ") || `${noun} ${r.member_id}`;
}

export function useDebounced<T>(v: T, ms = 300) {
  const [d, setD] = React.useState(v);
  React.useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = React.useState(false);
  React.useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1500);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <button type="button" aria-label={done ? "Copied" : `Copy ${label}`} title={done ? "Copied" : `Copy ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value).then(() => setDone(true)).catch(() => {});
      }}
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
      {done ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
    </button>
  );
}

/** Email + phone links for a table cell. Clicking them doesn't open the row. */
export function ContactCell({ email, phone }: { email: string | null; phone: string | null }) {
  return (
    <div className="min-w-0 space-y-0.5 text-[13px]">
      {email ? (
        <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()}
          className="flex max-w-[260px] items-center gap-1.5 truncate text-[#007a8a] hover:underline">
          <Mail className="size-3.5 shrink-0" aria-hidden /><span className="truncate">{email}</span>
        </a>
      ) : <span className="text-slate-400">No email</span>}
      {phone ? (
        <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 text-slate-600 hover:text-[#007a8a] hover:underline">
          <Phone className="size-3.5 shrink-0" aria-hidden />{phone}
        </a>
      ) : <span className="block text-xs text-slate-400">No phone</span>}
    </div>
  );
}

/** One line of contact detail in a drawer. */
export function ContactLine({ icon: Icon, children, copy, copyLabel }: {
  icon: React.ComponentType<{ className?: string }>; children: React.ReactNode; copy?: string | null; copyLabel?: string;
}) {
  return (
    <li className="flex min-h-7 items-center gap-2.5 text-sm text-slate-700">
      <Icon className="size-4 shrink-0 text-[#007a8a]" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {copy && <CopyButton value={copy} label={copyLabel ?? "value"} />}
    </li>
  );
}

const NOTE_MAX = 4000;

/** The outcomes an entry carries: every one when the campaign is multi-select. */
export function entryOutcomes<K extends string>(e: { outcome: K; outcomes?: K[] }): K[] {
  return e.outcomes?.length ? e.outcomes : [e.outcome];
}

/**
 * Outcome picker + note: used for a new entry and for editing one.
 *
 * Single-select (win-back) is a radio group. ``multiple`` (candidate outreach)
 * is a set of toggles — one call can be "Voicemail left" and "Follow up
 * required" — grouped under ``groups`` headings when the outcomes carry one.
 * Retired outcomes are never offered, but stay selected on an entry that
 * already has them.
 */
export function OutcomeForm<K extends string>({
  outcomes, initialOutcomes, initialNote = "", submitLabel, busy, error, onSubmit, onCancel, autoFocus, placeholder,
  multiple = false, groups,
}: {
  outcomes: OutcomeDef<K>[];
  initialOutcomes?: K[];
  initialNote?: string;
  submitLabel: string;
  busy: boolean;
  error?: string | null;
  onSubmit: (outcomes: K[], note: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  placeholder?: string;
  multiple?: boolean;
  groups?: Record<string, string>;
}) {
  const [picked, setPicked] = React.useState<K[]>(initialOutcomes ?? []);
  const [note, setNote] = React.useState(initialNote);
  const id = React.useId();

  const offered = outcomes.filter((o) => !o.retired || picked.includes(o.key));
  const pick = (k: K) => setPicked((cur) =>
    multiple ? (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]) : [k]);
  const needsNote = outcomes.filter((o) => o.needsNote && picked.includes(o.key));
  const noteMissing = needsNote.length > 0 && !note.trim();

  const sections: { key: string; title?: string; items: OutcomeDef<K>[] }[] = groups
    ? [...Object.keys(groups), "retired"]
        .map((g) => ({ key: g, title: g === "retired" ? "No longer used" : groups[g],
          items: offered.filter((o) => (o.retired ? "retired" : o.group) === g) }))
        .filter((sct) => sct.items.length)
    : [{ key: "all", items: offered }];

  const pill = (o: OutcomeDef<K>) => {
    const on = picked.includes(o.key);
    return (
      <button key={o.key} type="button"
        {...(multiple ? { "aria-pressed": on } : { role: "radio", "aria-checked": on })}
        onClick={() => pick(o.key)}
        className={cn(
          "rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#0f6f7c]",
          multiple && "px-2.5 py-1 text-[13px]",
          on ? TONE[o.tone] + " ring-2" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:text-slate-900",
        )}>
        {multiple && on && <Check className="-ml-0.5 mr-1 inline size-3.5 align-[-2px]" aria-hidden />}
        {o.label}
      </button>
    );
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (picked.length && !noteMissing) onSubmit(picked, note); }} className="space-y-3">
      {multiple ? (
        <div role="group" aria-label="Outcomes" className="space-y-2">
          {sections.map((sct) => (
            <div key={sct.key}>
              {sct.title && <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{sct.title}</p>}
              <div className="flex flex-wrap gap-1.5">{sct.items.map(pill)}</div>
            </div>
          ))}
          <p className="text-xs text-slate-500" aria-live="polite">
            {picked.length ? `${picked.length} selected — pick every one that applies` : "Pick every outcome that applies"}
          </p>
        </div>
      ) : (
        <div role="radiogroup" aria-label="Outcome" className="flex flex-wrap gap-1.5">
          {offered.map(pill)}
        </div>
      )}
      <div>
        <label htmlFor={`${id}-note`} className="mb-1 block text-xs font-medium text-slate-500">
          Note {needsNote.length
            ? <span className="font-medium text-[#ae232d]">(required for &ldquo;{needsNote[0].label}&rdquo;)</span>
            : <span className="font-normal">(optional)</span>}
        </label>
        <textarea id={`${id}-note`} value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={NOTE_MAX}
          autoFocus={autoFocus} aria-required={needsNote.length > 0}
          placeholder={placeholder ?? "What happened? e.g. asked us to call back after the 15th"}
          className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#0f6f7c] focus:outline-none focus:ring-2 focus:ring-[#0f6f7c]/20" />
      </div>
      {error && <p role="alert" className="text-sm text-[#ae232d]">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
            Cancel
          </button>
        )}
        <button type="submit" disabled={!picked.length || noteMissing || busy}
          className="rounded-lg bg-[#003d45] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004d57] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f6f7c]">
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

/** One entry on the outreach history, editable in place by its author. */
export function TimelineEntry<K extends string>({
  outcomes, entry, onSave, tag, multiple = false, groups,
}: {
  outcomes: OutcomeDef<K>[];
  entry: OutreachEntryBase<K>;
  onSave: (id: string, patch: { outcomes: K[]; note: string }) => Promise<unknown>;
  /** Extra context next to the outcome, e.g. the cycle a call was about. */
  tag?: React.ReactNode;
  multiple?: boolean;
  groups?: Record<string, string>;
}) {
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async (picked: K[], note: string) => {
    setBusy(true); setError(null);
    try {
      await onSave(entry.id, { outcomes: picked, note });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="relative pl-6">
      <span aria-hidden className="absolute left-[7px] top-2 size-2.5 rounded-full border-2 border-white bg-[#007a8a] ring-1 ring-[#007a8a]/30" />
      {editing ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <OutcomeForm outcomes={outcomes} initialOutcomes={entryOutcomes(entry)} initialNote={entry.note ?? ""} submitLabel="Save changes"
            busy={busy} error={error} onSubmit={save} onCancel={() => { setEditing(false); setError(null); }} autoFocus
            multiple={multiple} groups={groups} />
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {entryOutcomes(entry).map((k) => <OutcomeChipFor key={k} outcomes={outcomes} outcome={k} long />)}
            {tag}
            <span className="text-xs text-slate-500">
              {entry.actor?.name ?? "Former staff member"} · {fmtDateTime(entry.created_at)}{entry.edited ? " · edited" : ""}
            </span>
            {entry.can_edit && (
              <button type="button" onClick={() => setEditing(true)}
                className="ml-auto rounded-md px-2 py-0.5 text-xs font-medium text-[#007a8a] hover:bg-[#e6f0f1] focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
                Edit
              </button>
            )}
          </div>
          {entry.note && <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-800">{entry.note}</p>}
        </div>
      )}
    </li>
  );
}
